// ゴールツリーの導出ロジック。依存ゼロの純関数（scripts/test-goal-core.mjs で単体テスト）。
// 仕様（設計書 docs/superpowers/specs/2026-06-12-flow-goal-layer-design.md 部品1-4）:
// - アクティブツリー = deleted=false かつ status!=='dormant'。dormant の枝は丸ごと辿らない
// - 枝の重さ = アクティブツリー上の子孫（自分含まず）のうち status!=='done' の数
//   （done ノードの配下は辿る — done の親の下に未完了が残っていれば残弾として数える）
// - 分数進捗 = アクティブな子のうち done の数 / 子の数。子ゴールが無い葉は goalId 一致タスクで代替。
//   0/0 は null（表示しない）
// - あと◯日 = ローカル日付文字列 YYYY-MM-DD 同士の暦差（マイナス = 超過）。
//   両辺を UTC 0時にアンカーした純粋な日付演算で、「今日」は laneCore.todayIso()（ローカル）を渡す。
//   new Date().toISOString().slice(...) のような UTC 由来の「今日」は禁止（日付ズレ前科）

export type GoalStatus = "todo" | "in_progress" | "done" | "dormant";

export interface GoalLike {
  id: string;
  parentId: string | null;
  title: string;
  status: GoalStatus;
  dueDate: string | null;
  sortOrder: number;
  deleted: boolean;
  createdAt: string;
}

export interface GoalTaskLike {
  id: string;
  completed: boolean;
  deleted: boolean;
  goalId?: string | null;
}

/** アクティブ（非削除・非休眠）な子。sortOrder → createdAt 順 */
export function activeChildren<G extends GoalLike>(goals: G[], parentId: string | null): G[] {
  return goals
    .filter((g) => !g.deleted && g.status !== "dormant" && g.parentId === parentId)
    .sort((a, b) => a.sortOrder - b.sortOrder || (a.createdAt < b.createdAt ? -1 : 1));
}

/** 枝の重さバッジ: アクティブツリー上の未完了（todo/in_progress）子孫数。自分は含まない */
export function remainingDescendants(goals: GoalLike[], id: string): number {
  let count = 0;
  for (const child of activeChildren(goals, id)) {
    if (child.status !== "done") count += 1;
    count += remainingDescendants(goals, child.id);
  }
  return count;
}

/** 分数進捗。子ゴールがあれば done子/全子、無ければ goalId 一致タスクで done/全。0/0 は null */
export function fractionProgress(
  goals: GoalLike[],
  tasks: GoalTaskLike[],
  id: string
): { done: number; total: number } | null {
  const children = activeChildren(goals, id);
  if (children.length > 0) {
    return { done: children.filter((c) => c.status === "done").length, total: children.length };
  }
  const linked = tasks.filter((t) => !t.deleted && t.goalId === id);
  if (linked.length === 0) return null;
  return { done: linked.filter((t) => t.completed).length, total: linked.length };
}

/** ルート→id のパス（パンくず）。id が見つからなければ空配列。データ循環は hops ガードで打ち切り */
export function breadcrumbPath<G extends GoalLike>(goals: G[], id: string): G[] {
  const byId = new Map(goals.map((g) => [g.id, g] as const));
  const path: G[] = [];
  let cur = byId.get(id);
  let hops = 0;
  while (cur && hops < 100) {
    path.unshift(cur);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    hops += 1;
  }
  return path;
}

/** あと◯日（dueIso - todayIso、暦日差）。dueDate 無しは null */
export function daysLeft(dueIso: string | null, todayIso: string): number | null {
  if (!dueIso) return null;
  const [y1, m1, d1] = todayIso.split("-").map(Number);
  const [y2, m2, d2] = dueIso.split("-").map(Number);
  const a = Date.UTC(y1, m1 - 1, d1);
  const b = Date.UTC(y2, m2 - 1, d2);
  return Math.round((b - a) / 86400000);
}

/** チップ表示: あとN日 / 今日 / N日超過（超過は overdue=true で赤表示） */
export function daysLeftLabel(
  dueIso: string | null,
  todayIso: string
): { text: string; overdue: boolean } | null {
  const days = daysLeft(dueIso, todayIso);
  if (days === null) return null;
  if (days === 0) return { text: "今日", overdue: false };
  if (days < 0) return { text: `${-days}日超過`, overdue: true };
  return { text: `あと${days}日`, overdue: false };
}

/** 葉ゴール = アクティブな子ゴールが無い */
export function isLeaf(goals: GoalLike[], id: string): boolean {
  return activeChildren(goals, id).length === 0;
}

/** この枝のタスク（goalId 一致・未完了・未削除） */
export function branchTasks<T extends GoalTaskLike>(tasks: T[], goalId: string): T[] {
  return tasks.filter((t) => !t.deleted && !t.completed && t.goalId === goalId);
}
